import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Warning, CurrencyInr, ShieldWarning, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LeakageDetector({ compact = false }) {
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(5);

  const load = async (t = threshold) => {
    const { data } = await api.get(`/operators/leakage?threshold=${t}`);
    setData(data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="ktcl-card p-4 text-sm text-slate-500">Loading leakage analysis…</div>;

  const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

  return (
    <div className="ktcl-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-cap text-slate-500 flex items-center gap-1.5">
            <ShieldWarning size={12} className="text-[#EF4444]"/> Revenue Integrity
          </div>
          <h3 className="font-display text-lg font-bold">Leakage Detector · GPS vs. Claimed KM</h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="text-slate-500">Threshold</label>
          <select value={threshold} onChange={(e)=>{const t=+e.target.value; setThreshold(t); load(t);}}
                  className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs outline-none">
            {[3,5,7,10].map(t => <option key={t} value={t}>{`${t}%`}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Mini label="Operators Flagged" value={data.flagged_count} total={data.total_operators} color="#EF4444" icon={Warning}/>
        <Mini label="Potential Recovery" value={inr(data.total_potential_recovery)} color="#22C55E" icon={CurrencyInr}/>
        <Mini label="Avg. Variance" value={(data.rows.reduce((s,r)=>s+r.diff_pct,0)/Math.max(1,data.rows.length)).toFixed(1)+"%"} color="#F97316" icon={TrendUp}/>
      </div>

      <div className="overflow-auto scroll-thin max-h-72">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-xs text-slate-500">
            {"Operator,Claimed KM,GPS KM,Diff,Variance,Recovery,Severity,Action".split(",").map(h =>
              <th key={h} className="px-2 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {(compact ? data.rows.slice(0,5) : data.rows).map(r => (
              <tr key={r.operator_id} className={`border-t border-slate-100 ${r.flagged ? "bg-red-50/30" : ""}`}>
                <td className="px-2 py-2">
                  <div className="font-mono text-xs font-bold text-[#1E3A8A]">{r.operator_id}</div>
                  <div className="text-xs text-slate-600 truncate max-w-[180px]">{r.operator_name}</div>
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{r.claimed_km.toLocaleString("en-IN")}</td>
                <td className="px-2 py-2 text-right tabular-nums">{r.gps_km.toLocaleString("en-IN")}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold" style={{color: r.diff_km > 0 ? "#EF4444" : "#22C55E"}}>
                  {r.diff_km > 0 ? "+" : ""}{r.diff_km}
                </td>
                <td className="px-2 py-2 text-right">
                  <span className="font-semibold tabular-nums" style={{color: r.flagged ? "#EF4444" : "#22C55E"}}>
                    {r.diff_pct}%
                  </span>
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-xs">
                  {r.potential_recovery > 0 ? inr(r.potential_recovery) : "—"}
                </td>
                <td className="px-2 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    r.severity === "High" ? "bg-red-100 text-red-700" :
                    r.severity === "Medium" ? "bg-amber-100 text-amber-700" :
                    "bg-green-100 text-green-700"}`}>{r.severity}</span>
                </td>
                <td className="px-2 py-2 text-right">
                  {r.flagged && (
                    <button onClick={()=>toast.success(`Dispute case opened for ${r.operator_id}`)}
                            className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100">
                      Open Dispute
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Mini = ({ label, value, total, color, icon: Icon }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
    <div className="flex items-center justify-between">
      <div className="label-cap text-slate-500">{label}</div>
      <Icon size={14} weight="duotone" style={{color}}/>
    </div>
    <div className="font-display text-xl font-bold mt-1" style={{color}}>
      {value}{total !== undefined && <span className="text-sm text-slate-500 font-normal"> / {total}</span>}
    </div>
  </div>
);
